export function sameOriginEndpoint(endpoint: string): string {
  if (!endpoint || !endpoint.startsWith('/') || endpoint.startsWith('//')) {
    throw new Error('临床接口必须使用本站的绝对路径。');
  }
  const url = new URL(endpoint, window.location.origin);
  if (url.origin !== window.location.origin) {
    throw new Error('临床接口必须使用本站的绝对路径。');
  }
  return url.href;
}

export type ClinicalSession = {
  token: string;
  refreshToken?: string;
  expiresAt: number;
  username: string;
};

export class ClinicalSessionService {
  readonly STORAGE_KEY = 'ohif.clinical.session.v1';
  private current: ClinicalSession | null = null;
  private listeners = new Set<() => void>();
  private generation = 0;
  private refreshPromise: Promise<string> | null = null;
  private expiryTimer: ReturnType<typeof setTimeout> | undefined;
  private hardExpiryTimer: ReturnType<typeof setTimeout> | undefined;
  private refreshController: AbortController | undefined;
  private configuration = {
    loginEndpoint: '/api/clinical/login',
    refreshEndpoint: '/api/clinical/login/refresh',
  };

  constructor() {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    try {
      const stored = JSON.parse(sessionStorage.getItem(this.STORAGE_KEY) || 'null');
      if (
        typeof stored?.token === 'string' &&
        stored.token &&
        typeof stored.username === 'string' &&
        Number.isFinite(stored.expiresAt) &&
        stored.expiresAt > Date.now()
      ) {
        this.current = stored;
        this.scheduleExpiry();
      } else {
        sessionStorage.removeItem(this.STORAGE_KEY);
      }
    } catch {
      sessionStorage.removeItem(this.STORAGE_KEY);
    }
  }

  configure(configuration = {}) {
    this.configuration = { ...this.configuration, ...configuration };
  }

  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };
  getSnapshot = () => this.current;
  getVersion = () => this.generation;
  getCurrentToken = () =>
    this.current && this.current.expiresAt > Date.now() ? this.current.token : null;
  getAuthorizationHeader = () => {
    const token = this.getCurrentToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  private scheduleExpiry() {
    clearTimeout(this.expiryTimer);
    clearTimeout(this.hardExpiryTimer);
    const current = this.current;
    if (!current) {
      return;
    }
    const expire = () => {
      if (this.current !== current) {
        return;
      }
      const remaining = current.expiresAt - Date.now();
      if (remaining <= 0) {
        this.clearToken();
      } else {
        this.hardExpiryTimer = setTimeout(expire, Math.min(2147483647, remaining));
      }
    };
    expire();
    if (!current.refreshToken) {
      return;
    }
    const delay = current.expiresAt - Date.now() - 30000;
    this.expiryTimer = setTimeout(
      () => {
        if (this.current !== current) {
          return;
        }
        if (current.expiresAt - Date.now() > 30000) {
          this.scheduleExpiry();
        } else {
          void this.ensureToken().catch(() => {
            /* ensureToken clears this generation */
          });
        }
      },
      Math.min(2147483647, Math.max(1, delay))
    );
  }

  setToken(token: string, expiresUtc: string | Date, refreshToken?: string, username = '') {
    const expiresAt = new Date(expiresUtc).getTime();
    if (!token || !Number.isFinite(expiresAt) || expiresAt <= Date.now()) {
      throw new Error('登录响应缺少有效的医生会话或过期时间，请确认认证接口配置。');
    }
    this.current = { token, refreshToken, expiresAt, username };
    sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(this.current));
    this.scheduleExpiry();
    this.listeners.forEach(listener => listener());
  }

  clearToken = () => {
    this.generation++;
    this.current = null;
    this.refreshPromise = null;
    clearTimeout(this.expiryTimer);
    clearTimeout(this.hardExpiryTimer);
    this.refreshController?.abort();
    this.refreshController = undefined;
    sessionStorage.removeItem(this.STORAGE_KEY);
    this.listeners.forEach(listener => listener());
  };

  async login(username: string, password: string, signal?: AbortSignal) {
    const generation = ++this.generation;
    const body = new FormData();
    body.append('username', username);
    body.append('password', password);
    const response = await fetch(sameOriginEndpoint(this.configuration.loginEndpoint), {
      method: 'POST',
      body,
      credentials: 'same-origin',
      signal,
    });
    if (!response.ok) {
      throw new Error(
        response.status === 401 ? '用户名或密码错误。' : '登录服务暂不可用，请稍后重试。'
      );
    }
    const value = await response.json();
    if (String(value.Status) !== '0') {
      throw new Error('用户名或密码错误。');
    }
    if (signal?.aborted || generation !== this.generation) {
      throw new DOMException('Cancelled', 'AbortError');
    }
    this.setToken(value.Token, value.ExpiresUtc, value.RefreshToken, username);
  }

  async ensureToken(): Promise<string> {
    const current = this.current;
    if (!current) {
      throw new Error('医生会话已过期，请重新登录。');
    }
    if (current.expiresAt - Date.now() > 30000) {
      return current.token;
    }
    if (!current.refreshToken) {
      if (current.expiresAt > Date.now()) {
        return current.token;
      }
      this.clearToken();
      throw new Error('医生会话已过期，请重新登录。');
    }
    if (this.refreshPromise) {
      return this.refreshPromise;
    }
    const generation = this.generation;
    const body = new FormData();
    body.append('refreshToken', current.refreshToken);
    const controller = new AbortController();
    this.refreshController = controller;
    const refresh = (async () => {
      const response = await fetch(sameOriginEndpoint(this.configuration.refreshEndpoint), {
        method: 'POST',
        body,
        credentials: 'same-origin',
        signal: controller.signal,
      });
      if (!response.ok) {
        throw new Error('医生会话已过期，请重新登录。');
      }
      const value = await response.json();
      if (generation !== this.generation) {
        throw new DOMException('Cancelled', 'AbortError');
      }
      if (new Date(value.accessExpiresUtc).getTime() - Date.now() <= 30000) {
        throw new Error('刷新接口返回的会话有效期不足。');
      }
      this.setToken(
        value.accessToken,
        value.accessExpiresUtc,
        value.refreshToken || current.refreshToken,
        current.username
      );
      return value.accessToken;
    })();
    this.refreshPromise = refresh;
    try {
      return await refresh;
    } catch (error) {
      if (generation === this.generation) {
        this.clearToken();
      }
      throw error;
    } finally {
      if (this.refreshController === controller) {
        this.refreshController = undefined;
      }
      if (this.refreshPromise === refresh) {
        this.refreshPromise = null;
      }
    }
  }
}

const clinicalSession = new ClinicalSessionService();
export default clinicalSession;
